import { build, rebuild } from './build';
import config from './config';
import { arrowObserver, dotObserver, keyObserver, transitionEndObserver, touchObserver, autoPlayObserver, resizeObserver } from './events';
import { updateArrowClasses, updateArrows, updateDots } from './updates';
import { applyTransition, reduceTransition } from './animations';
import { changeSlide, slideHandler, swipeHandle, updateInfiniteSlides, cleanupFade } from './slide-handlers';
import { on, emit } from './event-hooks';
import { getCurrentSlide, next, previous } from './util';

export default createSlider;

const Slider = {
	currentSlide: 0,
	slideCount: 0,
	maxSlidePos: 0,
	minSlidePos: 0,
	previousSlide: 0,
	slideOffset: 0,
	swipePosition: 0,
	currentSwipePosition: 0,
	swipeDirection: 0,
	maxSwipePosition: 0,
	swipeIsDown: false,
	setupAnim: true,
	activeBreakpoint: 0,
	reducedAnimationApplied: false,
	animationUnapplied: false,
	init,

	elements: {},
	config: {},
	events: {},
	subscribers: {},
	originalOpions: null,
	responsiveOptions: {},
	breakpoints: [],

	changeSlide,
	on,
	getCurrentSlide,
	next,
	previous,
	play: () => {},
	pause: () => {}
};

function createSlider(selector, options, classes, arrows){
	const slider = Object.create(Slider);
	slider.init(selector, options,classes, arrows);
	return slider;
}

function init(selector, options, classes, arrows){
	const sliderElement = document.querySelector(selector);
	if(!sliderElement){
		return;
	}

	this.elements.sliderElement = sliderElement;

	this.config = Object.assign({}, config);
	this.events = Object.assign({}, this.config.events);

	if(options){
		this.config.options = Object.assign({}, this.config.options, options);
	}
	if(classes){
		this.config.classes = Object.assign({}, this.config.classes, classes);
	}
	if(arrows){
		this.config.arrows = Object.assign({}, this.config.arrows, arrows);
	}

	this.changeSlide = this.changeSlide.bind(this);

	registerBreapoints.call(this);
	checkBreakpoints.call(this, true);
}

function registerBreapoints(){
	if(!this.config.options.responsive || !this.config.options.responsive.length){
		return;
	}

	let responsiveBP = this.config.options.responsive.reduce((acc, el) => {
		if(acc[el.breakpoint]){
			return acc;
		}

		acc[el.breakpoint] = Object.assign({}, this.config.options, el.options);
		return acc;
	}, {});
	this.responsiveOptions = responsiveBP;
	this.originalOpions = this.config.options;

	let breakpoints = Object.keys(responsiveBP);
	breakpoints = breakpoints.sort((a, b) => this.config.options.mobileFirst ? a - b : b - a);
	this.breakpoints = breakpoints;
}

function checkBreakpoints(isInitial = false){
	const windowWidth = window.innerWidth;
	let compare = this.config.options.mobileFirst ? (a, b) => a > b : (a, b) => a < b;

	let targetBreakpoint = this.breakpoints.reduce((acc, breakpoint) => compare(windowWidth, breakpoint) ? breakpoint : acc, 0);

	if(targetBreakpoint === this.activeBreakpoint && !isInitial){
		return;
	}

	this.activeBreakpoint = targetBreakpoint;
	refresh.call(this, isInitial);
}

function refresh(isIntial = false){
	if(this.activeBreakpoint > 0){
		this.config.options = this.responsiveOptions[this.activeBreakpoint];
	}else{
		if(this.originalOpions){
			this.config.options = this.originalOpions;
		}
	}

	const buildMethod = isIntial ? build : rebuild;

	this.elements = buildMethod(this.config, this.elements.sliderElement);
	this.slideCount = this.elements.slides.length;
	this.currentSlide = 0;

	setupObservers.call(this, isIntial);
	setupState.call(this);
	updateArrows.call(this);
	updateArrowClasses.call(this);
	updateDots.call(this);
	reduceTransition.call(this);
	slideHandler.call(this);

	if(!isIntial){
		setTimeout(applyTransition.bind(this), 1);
	}
}

function setupState(){
	if(this.config.options.infinite && !this.config.options.fade){
		this.slideCount = this.slideCount - (2 * this.config.options.slidesToShow);
		this.slideOffset = this.config.options.slidesToShow;
		this.maxSlidePos = this.slideCount;
		this.minSlidePos = -this.config.options.slidesToShow;
	}else if(this.config.options.infinite && this.config.options.fade){
		this.maxSlidePos = this.slideCount;
		this.minSlidePos = -1;
	}else{
		this.maxSlidePos = this.slideCount;
		this.minSlidePos = 0;
	}

	setSlideWidth.call(this);
}

function setupObservers(isInitial){
	if(isInitial){
		resizeObserver()
			.subscribe(() => resize.call(this));

		transitionEndObserver(this.elements)
			.subscribe(() => {
				applyTransition.call(this);

				if(this.config.options.fade){
					cleanupFade.call(this);
				}else{
					updateInfiniteSlides.call(this);
				}

				if(!this.setupAnim){
					emit.call(this, this.events.slideChangeAnimEnd, getCurrentSlide.call(this));
				}

				this.setupAnim = false;
			});

		if(this.config.options.keyNavigation){
			keyObserver(this.elements)
				.subscribe(increment => this.changeSlide(increment, true));
		}

		if(this.config.options.swipe && !this.config.options.fade){
			touchObserver(this.elements)
				.subscribe(data => {
					swipeHandle.call(this, data);
				});
		}

		if(this.config.options.autoplay && this.config.options.infinite){
			const {play, pause} = autoPlayObserver(
				this.elements,
				this.config.options,
				increment => this.changeSlide(increment, true)
			);
			this.play = play;
			this.pause = pause;
		}
	}

	if(this.config.options.arrows){
		arrowObserver(this.elements)
			.subscribe(increment => this.changeSlide(increment, true));
	}

	if(this.config.options.dots){
		dotObserver(this.elements, this.config.classes.dot)
			.subscribe(slideNum => this.changeSlide(slideNum));
	}
}

function resize(){
	setSlideWidth.call(this);
	checkBreakpoints.call(this);
}

function setSlideWidth(){
	const {width} = this.elements.slides[0].getBoundingClientRect();
	this.slideWidth = width;
	if(this.config.options.infinite){
		this.maxSwipePosition = this.slideWidth * (this.maxSlidePos + this.config.options.slidesToShow);
	}else{
		this.maxSwipePosition = this.slideWidth * this.maxSlidePos;
	}
}



// WEBPACK FOOTER //
// ./node_modules/ui-slider/app/js/src/slider/index.js